import type {
  CreateJobResponse,
  CreateJobResponseViewModel,
  JobError,
  JobFile,
  JobFileViewModel,
  JobErrorViewModel,
  JobListItem,
  JobListResponse,
  JobListViewModel,
  JobOwner,
  JobOwnerViewModel,
  JobStatusResponse,
  JobViewModel,
  MappingJobNotification,
  MappingJobWebSocketMessage,
  ResultExportResponse,
  ResultExportViewModel,
} from "@models/job";

export const buildJobErrorViewModel = (
  error: JobError | null,
): JobErrorViewModel | null => {
  if (!error) return null;
  return {
    errorCode: error.error_code,
    message: error.message,
    description: error.description,
  };
};

export const buildJobFileViewModel = (file: JobFile): JobFileViewModel => ({
  fileIndex: file.file_index,
  status: file.status,
  inputKind: file.input_kind,
  filename: file.display_name,
  memberPath: file.member_path ?? null,
  hasInputPdf: file.has_input_pdf,
  error: buildJobErrorViewModel(file.error ?? null),
  sourceUnits: file.source_units ?? null,
  rowsTotal: file.rows_total,
  rowsOk: file.rows_ok,
  rowsFailed: file.rows_failed,
  rowsNoMatch: file.rows_no_match,
  acceptedCount: file.accepted_count,
});

export const buildJobViewModelFromStatus = (
  response: JobStatusResponse,
): JobViewModel => ({
  jobId: response.job_id,
  processId: response.process_id ?? null,
  pluginId: response.plugin_id,
  service: response.service,
  status: response.status,
  inputKind: response.input_kind,
  filename: response.filename,
  fileCount: response.file_count,
  filesDone: response.files_done,
  failedFiles: response.failed_files,
  rowsTotal: response.rows_total,
  rowsOk: response.rows_ok,
  rowsFailed: response.rows_failed,
  rowsNoMatch: response.rows_no_match,
  acceptedCount: response.accepted_count,
  error: buildJobErrorViewModel(response.error),
  createdAt: response.created_at,
  updatedAt: response.updated_at,
});

export const buildJobOwnerViewModel = (
  owner: JobOwner | null,
): JobOwnerViewModel | null => {
  if (!owner) return null;
  return {
    userId: owner.user_id,
    email: null,
    firstName: owner.first_name,
    lastName: owner.last_name,
  };
};

export const buildJobViewModelFromListItem = (
  item: JobListItem,
): JobViewModel => ({
  jobId: item.job_id,
  pluginId: item.plugin_id,
  service: item.service,
  status: item.status,
  inputKind: item.input_kind,
  filename: item.filename,
  fileCount: item.file_count,
  filesDone: item.files_done,
  failedFiles: item.failed_files,
  rowsTotal: item.rows_total,
  rowsOk: item.rows_ok,
  rowsFailed: item.rows_failed,
  rowsNoMatch: item.rows_no_match,
  acceptedCount: 0,
  error: buildJobErrorViewModel(item.error),
  owner: buildJobOwnerViewModel(item.created_by ?? null),
  createdAt: item.created_at,
  updatedAt: item.updated_at,
});

export const buildCreateJobResponseViewModel = (
  response: CreateJobResponse,
): CreateJobResponseViewModel => ({
  jobId: response.job_id,
  status: response.status,
  uploadUrl: response.upload_url,
  uploadContentType: response.upload_content_type,
  uploadExpiresAt: response.upload_expires_at,
  maxBytes: response.max_bytes,
});

export const buildResultExportViewModel = (
  response: ResultExportResponse,
): ResultExportViewModel => ({
  downloadUrl: response.download_url,
  filename: response.filename,
  rowCount: response.row_count,
});

export const buildJobListViewModel = (
  response: JobListResponse,
): JobListViewModel => ({
  jobs: response.jobs.map(buildJobViewModelFromListItem),
  cursor: response.cursor,
});

export const buildMappingJobNotification = (
  message: MappingJobWebSocketMessage,
): MappingJobNotification => {
  if (message.type === "mapping.job.progress") {
    return {
      type: message.type,
      jobId: message.job_id,
      status: message.status,
      fileIndex: message.file_index,
      sourceUnits: message.source_units,
      rowsOk: message.rows_ok,
      rowsFailed: message.rows_failed ?? 0,
      rowsTotal: message.rows_total,
    };
  }
  return {
    type: message.type,
    jobId: message.job_id,
    pluginId: message.plugin_id,
    status: message.status,
    resultUrlPath: message.result_url_path,
  };
};
